import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  LEGAL_DOCUMENT_TYPE_LABELS,
  LEGAL_PLATFORM_LABELS,
  LegalDocument,
  LegalDocumentType,
  LegalPlatform
} from '../../models/legal-document';
import { LegalDocumentService } from '../../../services/legal-document.service';

/**
 * Writes a draft: title, a short "what changed" shown to users above the text, and the text itself
 * in Markdown with a live preview beside it.
 *
 * Creates a draft when {@link document} is null (of {@link platform} and {@link type}), edits it
 * otherwise. Only drafts
 * reach this dialog; a published version is frozen and is changed by starting a new one.
 *
 * Unlike the catalog dialog it does not close on a backdrop click, and Escape only closes it while
 * nothing has been typed: losing a page of legal text to a stray click is worse than one more press
 * on Cancel.
 */
@Component({
  selector: 'app-legal-document-form-dialog',
  templateUrl: './legal-document-form-dialog.component.html',
  styleUrls: ['./legal-document-form-dialog.component.scss']
})
export class LegalDocumentFormDialogComponent implements OnChanges {

  @Input() isOpen = false;
  /** The draft to edit, or null to create one. */
  @Input() document: LegalDocument | null = null;
  /** The type of a new draft; ignored when editing. */
  @Input() type: LegalDocumentType = 'TERMS_OF_USE';
  /** The app a new draft belongs to; ignored when editing. */
  @Input() platform: LegalPlatform = 'DENTAL_LINK';

  @Output() saved = new EventEmitter<LegalDocument>();
  @Output() cancel = new EventEmitter<void>();

  title = '';
  changeSummary = '';
  content = '';

  saving = false;
  error: string | null = null;
  private initial = '';

  constructor(private legalService: LegalDocumentService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.title = this.document?.title ?? LEGAL_DOCUMENT_TYPE_LABELS[this.type];
      this.changeSummary = this.document?.changeSummary ?? '';
      this.content = this.document?.content ?? '';
      this.initial = this.snapshot();
      this.error = null;
      this.saving = false;
    }
  }

  get heading(): string {
    const what = `${LEGAL_PLATFORM_LABELS[this.document?.platform ?? this.platform]} `
      + LEGAL_DOCUMENT_TYPE_LABELS[this.document?.type ?? this.type];
    return this.document ? `Edit draft — ${what}` : `New ${what}`;
  }

  get dirty(): boolean {
    return this.snapshot() !== this.initial;
  }

  get canSave(): boolean {
    return !this.saving && this.title.trim() !== '' && this.content.trim() !== '';
  }

  save(): void {
    if (!this.canSave) {
      return;
    }
    const body = {
      title: this.title.trim(),
      content: this.content,
      changeSummary: this.changeSummary.trim() || null
    };
    this.saving = true;
    this.error = null;
    const call = this.document
      ? this.legalService.update(this.document.id, body)
      : this.legalService.create({ ...body, platform: this.platform, type: this.type });

    call.subscribe({
      next: (document) => {
        this.saving = false;
        this.saved.emit(document);
      },
      error: (err) => {
        this.saving = false;
        this.error = err?.error?.exceptionMessage || err?.error?.message || 'The draft could not be saved.';
      }
    });
  }

  close(): void {
    if (!this.saving) {
      this.cancel.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen && !this.dirty) {
      this.close();
    }
  }

  private snapshot(): string {
    return JSON.stringify([this.title, this.changeSummary, this.content]);
  }
}
